import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// 언어별 리소스 파일 import
import ko from '../locales/ko.json';
import en from '../locales/en.json';
import ja from '../locales/ja.json';
import zhCN from '../locales/zh-CN.json';
import zhTW from '../locales/zh-TW.json';
import vi from '../locales/vi.json';

// 사용 가능한 언어 목록
export const availableLanguages = {
    ko: '한국어',
    en: 'English',
    ja: '日本語',
    'zh-CN': '简体中文',
    'zh-TW': '繁體中文',
    vi: 'Tiếng Việt',
} as const;

export type LanguageCode = keyof typeof availableLanguages;

// 언어 코드와 CountrySelector 코드 매핑
export const countryCodeToLanguageCode: Record<string, LanguageCode> = {
    KR: 'ko',
    US: 'en',
    JP: 'ja',
    CN: 'zh-CN',
    TW: 'zh-TW',
    VN: 'vi',
};

export const languageCodeToCountryCode: Record<LanguageCode, string> = {
    ko: 'KR',
    en: 'US',
    ja: 'JP',
    'zh-CN': 'CN',
    'zh-TW': 'TW',
    vi: 'VN',
};

// 기본 언어 설정
const defaultLanguage: LanguageCode = 'ko';

// localStorage에서 저장된 언어 우선 감지, 없으면 브라우저 언어 감지
const getInitialLanguage = (): LanguageCode => {
    // 1. localStorage에서 저장된 언어 확인 (language-storage)
    const storedLanguage = localStorage.getItem('language-storage');
    if (storedLanguage) {
        try {
            const parsed = JSON.parse(storedLanguage);
            // Zustand persist 구조에 맞게 수정
            const currentLanguage = parsed.state?.currentLanguage || parsed.currentLanguage;
            if (currentLanguage && Object.keys(availableLanguages).includes(currentLanguage)) {
                return currentLanguage as LanguageCode;
            }
        } catch (e) {
            console.warn('Failed to parse stored language:', e);
        }
    }

    // 2. i18next의 localStorage 확인
    const i18nextLng = localStorage.getItem('i18nextLng');
    if (i18nextLng && Object.keys(availableLanguages).includes(i18nextLng)) {
        return i18nextLng as LanguageCode;
    }

    // 3. 브라우저 언어 감지
    const browserLang = navigator.language.toLowerCase();

    // 정확한 매칭 시도 (예: zh-CN, zh-TW)
    if (Object.keys(availableLanguages).includes(browserLang)) {
        return browserLang as LanguageCode;
    }

    // 언어 코드만으로 매칭 시도 (예: ko, en, ja)
    const langCode = browserLang.split('-')[0];
    if (Object.keys(availableLanguages).includes(langCode)) {
        return langCode as LanguageCode;
    }

    // 중국어 특별 처리
    if (langCode === 'zh') {
        if (browserLang.includes('tw') || browserLang.includes('hk')) {
            return 'zh-TW';
        }
        return 'zh-CN';
    }

    return defaultLanguage;
};

// i18n 초기화 함수
export const initializeI18n = (initialLanguage?: LanguageCode) => {
    const languageToUse = initialLanguage || getInitialLanguage();

    return i18n
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
            resources: {
                ko: { translation: ko },
                en: { translation: en },
                ja: { translation: ja },
                'zh-CN': { translation: zhCN },
                'zh-TW': { translation: zhTW },
                vi: { translation: vi },
            },
            lng: languageToUse,
            fallbackLng: defaultLanguage,
            debug: false,

            // 언어 감지 설정
            detection: {
                order: ['localStorage', 'navigator', 'htmlTag'],
                caches: ['localStorage'],
                lookupLocalStorage: 'i18nextLng',
            },

            // 인터폴레이션 설정
            interpolation: {
                escapeValue: false, // React는 이미 XSS를 방지하므로 false
            },

            // 네임스페이스 설정
            ns: ['translation'],
            defaultNS: 'translation',

            // 반응형 설정
            react: {
                useSuspense: false, // React 18+ 호환성을 위해 false
            },
        });
};

// 기본 초기화 (fallback)
initializeI18n();

export default i18n;
